import React, { useEffect, useState } from "react";

import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  BarChart, Bar,        // keep for Top Reasons
  ScatterChart, Scatter, // add these
  XAxis, YAxis, Tooltip, ZAxis, ReferenceLine,
} from "recharts";
import { Card, CardBody, CardHeader, Spinner, Row, Col } from "reactstrap";

/* ---------------- HELPERS ---------------- */
const SHIFT_TIME_RANGES = {
  1: { start: 6 * 60, end: 14 * 60 },   // 06:00–14:00
  2: { start: 14 * 60, end: 22 * 60 },  // 14:00–22:00
  3: { start: 22 * 60, end: 30 * 60 },  // 22:00–06:00 (next day)
};

const timeToMinutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToShiftTime = (value, shiftStart) => {
  const total = (shiftStart + value) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};



/* ---------------- TOOLTIP ---------------- */

const TimelineTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div className="bg-white p-2 border rounded">
      <p><b>Start:</b> {d.start_time}</p>
      <p><b>End:</b> {d.end_time}</p>
      <p><b>Duration:</b> {d.duration} min</p>
      <p><b>Reasons:</b></p>
      <ul style={{ paddingLeft: 16 }}>
        {d.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
    </div>
  );
};

/* ---------------- COMPONENT ---------------- */

const Breakdown = ({ machineId, date, shift }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);


 useEffect(() => {
  if (!machineId || !date || !shift) return;

  let intervalId;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.hanoch.in/api/mcc/machine-downtime-reasons/?machine_id=${machineId}&date=${date}&shift=${shift}`
      );
      const result = await res?.json();
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 initial call
  fetchData();

  // 🔁 live update every 1 minute
  intervalId = setInterval(fetchData, 60 * 1000);

  // 🧹 cleanup
  return () => {
    clearInterval(intervalId);
  };
}, [machineId, date, shift]);

//   if (loading) {
//     return (
//       <Card>
//         <CardBody className="text-center">
//           <Spinner size="sm" /> Loading downtime...
//         </CardBody>
//       </Card>
//     );
//   }

  if (!data) return null;

  /* ---------------- TRANSFORMS ---------------- */

  // Timeline data

  const shiftStart = SHIFT_TIME_RANGES[shift].start;
const shiftEnd = SHIFT_TIME_RANGES[shift].end;
const shiftDuration = shiftEnd - shiftStart;

const timelineData = data?.data?.downtimes?.map((d) => {
  let startMin = timeToMinutes(d.start);
  let endMin = timeToMinutes(d.end);

  // Night shift handling
  if (shift === 3 && endMin < startMin) {
    endMin += 24 * 60;
  }

  return {
    start: startMin - shiftStart,     // X position (shift-relative)
    duration: endMin - startMin,      // Y value
    start_time: d.start.slice(0, 5),
    end_time: d.end.slice(0, 5),
    reasons: d.reasons?.map(r => r.description) || [],
  };
});




  

const exportTimelineToExcel = () => {
  if (!data?.data?.downtimes?.length) return;

  const exportRows = [];

  data.data.downtimes.forEach((d) => {
    const durationMin = (() => {
      const [sh, sm] = d.start.split(":").map(Number);
      const [eh, em] = d.end.split(":").map(Number);
      let startM = sh * 60 + sm;
      let endM = eh * 60 + em;
      if (shift === 3 && endM < startM) endM += 24 * 60;
      return endM - startM;
    })();

    const reasons = d.reasons?.map((r) => r.description) || [];

    if (reasons.length === 0) {
      exportRows.push({
        Station: "",
        Reason: "",
        "Downtime Hours": (durationMin / 60).toFixed(2),
        Date: date,
        Shift: shift,
      });
    } else {
      reasons.forEach((r) => {
        // Extract station from reason like "ST.32 FERRITE FEEDING"
        const parts = r.split(" ");
        const station = parts[0]?.startsWith("ST.")
          ? parts[0].replace("ST.", "Station ")
          : parts[0];
        const reasonText = parts[0]?.startsWith("ST.")
          ? parts.slice(1).join(" ")
          : r;

        exportRows.push({
          Station: station,
          Reason: reasonText,
          "Downtime Hours": (durationMin / 60).toFixed(2),
          Date: date,
          Shift: shift,
        });
      });
    }
  });

  const worksheet = XLSX.utils.json_to_sheet(exportRows);

  // Style header row width
  worksheet["!cols"] = [
    { wch: 15 }, // Station
    { wch: 35 }, // Reason
    { wch: 16 }, // Downtime Hours
    { wch: 14 }, // Date
    { wch: 8 },  // Shift
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Breakdown Details");

  XLSX.writeFile(
    workbook,
    `Downtime_${machineId}_${date}_Shift${shift}.xlsx`
  );
};

  return (
    <>
    <Row>
      {/* ---------------- TIMELINE ---------------- */}
      <Col md={12}>
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
  <div>
    <h5 className="mb-0">Downtime Time graph</h5>
    <small className="text-muted">
      Total: {data?.summary?.total_downtime.minutes} (
      {data?.summary?.total_downtime.hours})
    </small>
  </div>

  <button
    className="btn btn-sm btn-outline-success"
    onClick={exportTimelineToExcel}
  >
    Export Excel
  </button>
</CardHeader>


          <CardBody >
             <div style={{ position: "relative", zIndex: 10 }}>
            <ResponsiveContainer width="100%" height={220}>
  <ScatterChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
    <XAxis
      dataKey="start"
      type="number"
      domain={[0, shiftDuration]}
      ticks={Array.from({ length: shiftDuration / 60 + 1 }, (_, i) => i * 60)}
      tickFormatter={(v) => minutesToShiftTime(v, shiftStart)}
    />
    <YAxis
      dataKey="duration"
      type="number"
      label={{ value: "Duration (min)", angle: -90, position: "insideLeft" }}
    />
    <ZAxis range={[60, 60]} />
    <Tooltip content={<TimelineTooltip />} />

    {/* Reference lines for every hour */}
    {Array.from({ length: shiftDuration / 60 + 1 }, (_, i) => (
      <ReferenceLine key={i} x={i * 60} stroke="#e5e7eb" strokeDasharray="3 3" />
    ))}

    <Scatter
      data={timelineData}
      fill="#103845"
      line={{ stroke: "#4fd3ff", strokeWidth: 0.5 }}
      lineType="joint"
      shape="circle"
    />
  </ScatterChart>
</ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default Breakdown;
